/**
 * 🧑‍💻 SWE-6: 회원 탈퇴 모달
 * 
 * 삭제 대상 데이터:
 * - subscriptions: 정기결제 취소
 * - billing_keys: 빌링키 무효화
 * - devices: 등록 기기 삭제
 * - refresh_tokens: 토큰 무효화
 * - licenses: 라이선스 삭제
 * - settings_sync: 동기화 설정 삭제
 * - usage_logs: 사용 기록 삭제
 * - device_events: 기기 이벤트 삭제
 * - audit_logs: 감사 로그 익명화
 * - payment_history: 결제 내역 익명화 (법적 보관)
 * - auth.users: Supabase 계정 삭제
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';

interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function DeleteAccountModal({ isOpen, onClose }: DeleteAccountModalProps) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [step, setStep] = useState<'confirm' | 'password' | 'deleting' | 'error'>('confirm');
  const [password, setPassword] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [deletionProgress, setDeletionProgress] = useState<string[]>([]);

  const CONFIRM_TEXT = '계정 삭제';

  const resetState = () => {
    setStep('confirm');
    setPassword('');
    setConfirmText('');
    setError(null);
    setDeletionProgress([]);
  };

  const handleClose = () => {
    if (step !== 'deleting') {
      resetState();
      onClose();
    }
  };

  const handleConfirm = () => {
    if (confirmText === CONFIRM_TEXT) {
      setStep('password');
    }
  };

  const handleDelete = async () => {
    if (!user?.email) return;

    setStep('deleting');
    setError(null);
    
    try {
      // 1. 비밀번호 재인증
      setDeletionProgress(prev => [...prev, '비밀번호 확인 중...']);
      const { error: authError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: password,
      });
      
      if (authError) {
        throw new Error('비밀번호가 올바르지 않습니다.');
      }
      setDeletionProgress(prev => [...prev, '✓ 비밀번호 확인 완료']);

      // 2. 서버 측 계정 삭제 RPC 호출
      setDeletionProgress(prev => [...prev, '계정 데이터 삭제 중...']);
      
      const { error: deleteError } = await supabase.rpc('delete_user_account', {
        p_user_id: user.id,
        p_reason: 'user_requested'
      });

      if (deleteError) {
        console.error('Delete account error:', deleteError);
        throw new Error(deleteError.message || '계정 삭제 중 오류가 발생했습니다.');
      }
      
      setDeletionProgress(prev => [
        ...prev, 
        '✓ 구독 정보 삭제',
        '✓ 빌링 정보 삭제',
        '✓ 기기 정보 삭제',
        '✓ 라이선스 삭제',
        '✓ 사용 기록 삭제',
      ]);

      // 3. Auth 사용자 삭제 (Edge Function)
      setDeletionProgress(prev => [...prev, '인증 정보 삭제 중...']);
      
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        try {
          const response = await fetch(
            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-auth-user`,
            {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${session.access_token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ user_id: user.id }),
            }
          );
          
          if (response.ok) {
            setDeletionProgress(prev => [...prev, '✓ 인증 정보 삭제']);
          } else {
            // Edge Function 실패해도 데이터는 이미 삭제됨
            console.warn('Auth user deletion via Edge Function failed, but data is already deleted');
            setDeletionProgress(prev => [...prev, '⚠ 인증 정보 삭제 지연 (자동 처리 예정)']);
          }
        } catch (e) {
          console.warn('Edge Function call failed:', e);
          setDeletionProgress(prev => [...prev, '⚠ 인증 정보 삭제 지연 (자동 처리 예정)']);
        }
      }

      setDeletionProgress(prev => [...prev, '✓ 계정 삭제 완료']);

      // 4. 로그아웃 및 리다이렉트
      await new Promise(resolve => setTimeout(resolve, 1500)); // 사용자가 진행 상황을 볼 수 있도록
      await logout();
      navigate('/', { replace: true });
      
    } catch (err) {
      setError(err instanceof Error ? err.message : '알 수 없는 오류가 발생했습니다.');
      setStep('error');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-zinc-700 bg-red-50 dark:bg-red-900/20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 dark:bg-red-900/50 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-bold text-red-700 dark:text-red-400">
                계정 삭제
              </h2>
              <p className="text-sm text-red-600/80 dark:text-red-400/80">
                이 작업은 되돌릴 수 없습니다
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-5">
          {step === 'confirm' && (
            <>
              <div className="mb-6">
                <h3 className="font-medium text-gray-900 dark:text-white mb-3">
                  삭제되는 데이터:
                </h3>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                  <li className="flex items-center gap-2">
                    <span className="text-red-500">•</span>
                    모든 구독 및 결제 정보
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-red-500">•</span>
                    등록된 기기 및 라이선스
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-red-500">•</span>
                    사용 기록 및 설정
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-red-500">•</span>
                    계정 및 프로필 정보
                  </li>
                </ul>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  확인을 위해 "<span className="text-red-600 font-bold">{CONFIRM_TEXT}</span>"를 입력하세요
                </label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder={CONFIRM_TEXT}
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-zinc-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent dark:bg-zinc-800 dark:text-white"
                />
              </div>
            </>
          )}

          {step === 'password' && (
            <>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                보안을 위해 비밀번호를 다시 입력해주세요.
              </p>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  비밀번호
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="비밀번호 입력"
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-zinc-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent dark:bg-zinc-800 dark:text-white"
                  autoFocus
                />
              </div>
            </>
          )}

          {step === 'deleting' && (
            <div className="py-4">
              <div className="flex items-center justify-center mb-4">
                <div className="w-8 h-8 border-4 border-red-500 border-t-transparent rounded-full animate-spin" />
              </div>
              <div className="space-y-2">
                {deletionProgress.map((msg, idx) => (
                  <p 
                    key={idx} 
                    className={`text-sm ${msg.startsWith('✓') ? 'text-green-600 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'}`}
                  >
                    {msg}
                  </p>
                ))}
              </div>
            </div>
          )}

          {step === 'error' && (
            <div className="py-4">
              <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                <p className="text-sm text-red-700 dark:text-red-400">
                  {error}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-zinc-700 bg-gray-50 dark:bg-zinc-800/50 flex justify-end gap-3">
          {step !== 'deleting' && (
            <button
              onClick={handleClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-zinc-700 rounded-lg transition-colors"
            >
              취소
            </button>
          )}
          
          {step === 'confirm' && (
            <button
              onClick={handleConfirm}
              disabled={confirmText !== CONFIRM_TEXT}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              계속
            </button>
          )}
          
          {step === 'password' && (
            <button
              onClick={handleDelete}
              disabled={!password}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              영구 삭제
            </button>
          )}
          
          {step === 'error' && (
            <button
              onClick={() => setStep('password')}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              다시 시도
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
